import { useRef, useState } from 'react';
import tw, { css } from 'twin.macro';
import LabelWithTooltip from '@/components/labelWithTooltip';
import ArrowUp from '@assets/svgs/auth/input-arrow.svg';

/**
 * Input component – form input with optional label, icons, validation message and number stepper.
 *
 * @param {Object} props
 * @param {string} props.name - Form field name (used with register).
 * @param {Function} props.register - React Hook Form register function.
 * @param {string} [props.placeholder] - Input placeholder text.
 * @param {string} [props.errorMessage] - Validation error message to display below the input.
 * @param {string} [props.type='string'] - Input type (e.g. 'text', 'number').
 * @param {React.ReactNode} [props.firstIcon] - Icon or element rendered before the input.
 * @param {React.ReactNode} [props.lsatIcon] - Icon or element rendered after the input (replaces number arrows when provided).
 * @param {Object|import('twin.macro').TwStyle} [props.inputStye] - Additional styles for the input element.
 * @param {Object|import('twin.macro').TwStyle} [props.containerSTyle] - Additional styles for the input container.
 * @param {Function} [props.onBlur] - Optional blur handler; called in addition to register's onBlur.
 * @param {React.RefObject} [props.inputRef] - Ref attached to the root wrapper div.
 * @param {number} [props.max] - Max value when type is 'number'.
 * @param {number} [props.min] - Min value when type is 'number'.
 * @param {number|string} [props.step] - Step value when type is 'number'.
 * @param {string} [props.defaultValue=''] - Default value for the input.
 * @param {Object} [props.validation] - Validation rules passed to register (e.g. required, min, max).
 * @param {boolean} [props.hideErrorMessage=false] - When true, the error message is not rendered.
 * @param {boolean} [props.showArrow=true] - When true and type is 'number', shows increment/decrement arrows.
 * @param {string} [props.label] - Label text shown above the input.
 * @param {string} [props.tooltipText] - Tooltip text for the label (when hasTooltip is true).
 * @param {boolean} [props.hasTooltip] - When true, shows a tooltip next to the label.
 * @param {boolean} [props.isRequired=false] - When true, marks the label with a red asterisk.
 * @param {Object|import('twin.macro').TwStyle} [props.labelStyle] - Additional styles for the label.
 * @param {Object|import('twin.macro').TwStyle} [props.rootElementStyle] - Additional styles for the root wrapper.
 * @param {Object|import('twin.macro').TwStyle} [props.disabledStyle] - Additional styles when disabled.
 * @param {boolean} [props.disabled] - When true, disables the input.
 * @param {Object|import('twin.macro').TwStyle} [props.labelRootElementStyle] - Container styles for the label/tooltip row.
 * @param {boolean} [props.focusOnContainer=false] - When true, clicking the container focuses the input.
 * @param {Object|import('twin.macro').TwStyle} [props.errorMessageStyle] - Additional styles for the error message.
 * Any other props are spread onto the inner input element.
 * @returns {React.ReactElement}
 */
const Input = ({
  name,
  register,
  placeholder,
  errorMessage,
  type = 'string',
  firstIcon,
  lsatIcon,
  inputStye,
  containerSTyle,
  onBlur,
  inputRef,
  max,
  min,
  step,
  defaultValue = '',
  validation,
  hideErrorMessage = false,
  showArrow = true,
  label,
  tooltipText,
  hasTooltip,
  isRequired = false,
  labelStyle,
  rootElementStyle,
  disabledStyle,
  disabled,
  labelRootElementStyle,
  focusOnContainer = false,
  errorMessageStyle,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputElementRef = useRef(null);
  const registerResult = register(name, validation);

  return (
    <div ref={inputRef} css={[tw`space-y-2 `, rootElementStyle]}>
      {label && (
        <LabelWithTooltip
          label={label}
          tooltipText={tooltipText}
          hasTooltip={hasTooltip}
          labelStyle={labelStyle}
          containerStyle={labelRootElementStyle}
          isRequired={isRequired}
        />
      )}
      <div
        tw="flex items-center hover:bg-neutral_50 border-border_stroke overflow-hidden transition-all bg-white duration-300 border rounded-md "
        css={[
          css`
            ${containerSTyle}
          `,
          errorMessage
            ? tw`border-error bg-error_50 hover:bg-error_50`
            : isFocused
              ? tw`border-Primary_600`
              : tw`border-border_stroke`,
          disabled && [tw`hover:bg-white`, disabledStyle],
        ]}
        onClick={focusOnContainer ? () => inputElementRef.current?.focus() : undefined}
      >
        {firstIcon}
        <input
          name={name}
          ref={el => {
            registerResult.ref(el);
            inputElementRef.current = el;
          }}
          css={[
            tw`w-full focus:border-none text-sm font-medium focus:outline-none bg-inherit outline-0 px-4 py-[0.875rem]  [line-height: normal]  placeholder:text-text_primary/50`,
            tw`max-md:py-2.5 max-md:[line-height: 1.25rem]`,
            inputStye,
            errorMessage && tw`bg-error_50`,
          ]}
          autoComplete="off"
          placeholder={placeholder}
          type={type}
          step={step}
          onFocus={() => setIsFocused(true)}
          onBlur={e => {
            registerResult.onBlur(e);
            if (onBlur) {
              onBlur(e);
            }
            setIsFocused(false);
          }}
          {...(type === 'number' && { onWheel: e => e.currentTarget.blur() })}
          defaultValue={defaultValue}
          min={min}
          max={max}
          onChange={registerResult.onChange}
          disabled={disabled}
          {...rest}
        />
        {lsatIcon ? (
          lsatIcon
        ) : type === 'number' && showArrow ? (
          <div tw="flex flex-col h-full gap-2 px-3 py-3 border-l-[1px] border-stroke">
            <img loading="lazy" decoding="async"
              src={ArrowUp}
              alt="arrow-up"
              onClick={() => {
                const input = document.querySelector(`input[name='${name}']`);
                if (input) {
                  input.value = Number(input.value || 0) + 1;
                }
                input && input.dispatchEvent(new Event('input', { bubbles: true }));
              }}
            />
            <img loading="lazy" decoding="async"
              tw="rotate-180"
              src={ArrowUp}
              alt="arrow-up"
              onClick={() => {
                const input = document.querySelector(`input[name='${name}']`);
                if (input) {
                  input.value = Number(input.value || 0) - 1;
                }
                input && input.dispatchEvent(new Event('input', { bubbles: true }));
              }}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      {!hideErrorMessage && errorMessage && (
        <span css={[tw`mt-1 text-sm text-error`, errorMessageStyle]}>{errorMessage}</span>
      )}
    </div>
  );
};
export default Input;
